import React, { useState, useRef, useEffect } from 'react'
import { FieldInputProps, FormikProps } from 'formik'

import styles from './FormSelect.module.scss'

interface Option {
  value: string
  label: string
}

interface Props {
  field: FieldInputProps<string>
  form: FormikProps<any>
  id: string
  label: string
  name: string
  options: Option[]
  placeholder?: string
  isRequired?: boolean
  error?: string
}

export const FormSelect: React.FC<Props> = ({
  field,
  form,
  id,
  label,
  name,
  options,
  placeholder = 'Please select one',
  isRequired,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const selectedOption = options.find((opt) => opt.value === field.value)

  const handleSelect = (value: string) => {
    setIsOpen(false)

    form.setFieldTouched(name, true, false)
    form.setFieldValue(name, value, true)
  }

  return (
    <div className={styles.SelectWrapper} ref={wrapperRef}>
      <label htmlFor={id} className={styles.Label}>
        {label} {isRequired && '*'}
      </label>
      <div
        className={`${styles.SelectBox} ${error ? styles.Error : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={field.value ? styles.Selected : styles.Placeholder}>
          {selectedOption?.label || placeholder}
        </span>
        <span className={styles.Arrow}>{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className={styles.OptionsList}>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={styles.Option}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {typeof error === 'string' && (
        <div className={styles.ErrorMessage}>{error}</div>
      )}
    </div>
  )
}
